import React, { useLayoutEffect, useRef } from "react";

/** A block that hugs its text. CSS `w-fit` stops working once text wraps:
 * the box stays at its max width and leaves empty space to the right of the
 * shorter wrapped lines. This measures the rendered line boxes and sets the
 * width to the right edge of the widest line, re-measuring on resize. */
export default function ShrinkWrapPanel({ className = "", children }) {
  const ref = useRef(null);

  const measure = () => {
    const panel = ref.current;
    if (!panel) return;
    // Let text wrap naturally at the max width before measuring
    panel.style.width = "";
    const { left } = panel.getBoundingClientRect();
    let right = left;
    const walker = document.createTreeWalker(panel, NodeFilter.SHOW_TEXT);
    const range = document.createRange();
    while (walker.nextNode()) {
      range.selectNodeContents(walker.currentNode);
      for (const rect of range.getClientRects()) right = Math.max(right, rect.right);
    }
    if (right === left) return;
    const style = getComputedStyle(panel);
    const trailing = parseFloat(style.paddingRight) + parseFloat(style.borderRightWidth);
    // +1px guards against sub-pixel rounding re-wrapping the last word
    panel.style.width = `${Math.ceil(right - left + trailing) + 1}px`;
  };

  // Re-measure after every render in case the copy changed
  useLayoutEffect(measure);

  useLayoutEffect(() => {
    const parent = ref.current?.parentElement;
    if (!parent) return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(parent);
    document.fonts?.ready.then(measure);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}
